import {
  SET_SNAPSHOT_VERSIONS,
  SET_SNAPSHOT_SYNCING,
  SET_SNAPSHOT_LAST_BACKUP,
  SET_SNAPSHOT_ERROR,
  SET_SNAPSHOT_BACKUP_STATUS,
  SET_SNAPSHOT_VIEW_MODE,
  CLEAR_SNAPSHOT_VIEW_MODE,
  SET_SNAPSHOT_SHARES,
  REMOVE_SNAPSHOT_SHARE,
  CLEAR_ALL,
} from '../constants';
import type { BackupStatus } from '../../utilities/snapshotStorage';

export interface SnapshotVersion {
  id: number;
  project_id: number | string;
  account_id: string;
  version: number;
  checksum: string;
  size_bytes: number;
  device_id: string | null;
  note: string | null;
  created_at: string;
}

export interface SnapshotViewMode {
  project_id: string;
  version: number;
  entered_at: string;
  created_at: string;
}

export interface SnapshotShare {
  code: string;
  created_at: number | null;
  expires_at: number | null;
  max_uses: number | null;
  hit_count: number;
  remaining_uses: number | null;
  status: 'active' | 'expired' | 'exhausted';
  /**
   * Paired wallet-pass code + ready-to-use URL for sharing the Health
   * Passport to the recipient. Populated for shares created after the
   * pass-pairing change; null for legacy shares (mobile hides the
   * wallet-pass action when null).
   */
  pass_code?: string | null;
  pass_url?: string | null;
}

interface SnapshotsState {
  /** Keyed by project_id string — most-recent versions first. */
  versions: Record<string, SnapshotVersion[]>;
  syncing: boolean;
  /** ISO8601 timestamp of the last successful backup, keyed by project_id. */
  lastBackup: Record<string, string>;
  error: string | null;
  /** Live backup status per project. Array contains one entry per storage location. */
  backupStatus: Record<string, BackupStatus[]>;
  /**
   * When non-null, the app is displaying a decrypted snapshot in read-only
   * "View Mode" — live data is not being shown until the user exits.
   */
  viewMode: SnapshotViewMode | null;
  /** Active + historical share links, keyed by project_id. */
  shares: Record<string, SnapshotShare[]>;
}

const initialState: SnapshotsState = {
  versions: {},
  syncing: false,
  lastBackup: {},
  error: null,
  backupStatus: {},
  viewMode: null,
  shares: {},
};

export default function snapshotsReducer(
  state = initialState,
  { type, payload }: { type: string; payload?: any },
): SnapshotsState {
  switch (type) {
    case CLEAR_ALL:
      return initialState;

    case SET_SNAPSHOT_VERSIONS:
      return {
        ...state,
        versions: { ...state.versions, [String(payload.project_id)]: payload.versions },
      };

    case SET_SNAPSHOT_SYNCING:
      return { ...state, syncing: payload };

    case SET_SNAPSHOT_LAST_BACKUP:
      return {
        ...state,
        lastBackup: { ...state.lastBackup, [String(payload.project_id)]: payload.timestamp },
      };

    case SET_SNAPSHOT_ERROR:
      return { ...state, error: payload };

    case SET_SNAPSHOT_BACKUP_STATUS:
      return {
        ...state,
        backupStatus: { ...state.backupStatus, [String(payload.project_id)]: payload.statuses },
      };

    case SET_SNAPSHOT_VIEW_MODE:
      return { ...state, viewMode: payload };

    case CLEAR_SNAPSHOT_VIEW_MODE:
      return { ...state, viewMode: null };

    case SET_SNAPSHOT_SHARES:
      return {
        ...state,
        shares: { ...state.shares, [String(payload.project_id)]: payload.shares },
      };

    case REMOVE_SNAPSHOT_SHARE: {
      const key = String(payload.project_id);
      const existing = state.shares[key] ?? [];
      return {
        ...state,
        shares: {
          ...state.shares,
          [key]: existing.map((s) => (s.code === payload.code ? { ...s, status: 'expired' } : s)),
        },
      };
    }

    default:
      return state;
  }
}
